import {
  Dom, gsap, rafCallback, Events, ScrollToPlugin
} from '@univers-agency/jupiter'
import Player from '@vimeo/player'
import Toolbar from './toolbar'
import Bubbles from '../Bubbles'
import Animations from './animations'

gsap.registerPlugin(ScrollToPlugin)

const AUTOPLAY = true
const CONFETTI = true
const SOUND = true
const SCROLL_DURATION = 90 // was 120

export default class Parade {
  constructor (app) {
    this.app = app

    this.$el = Dom.find('.parade')
    this.$overlayLayer = Dom.find('.layer-overlay')
    this.$bgLayer = Dom.find('.layer-background')
    this.$mainLayer = Dom.find('.layer-main')
    this.$playPause = Dom.find('.toolbar__playpause')
    this.$btnPlay = Dom.find('.toolbar__playpause__play')
    this.$btnPause = Dom.find('.toolbar__playpause__pause')

    this.toolbar = new Toolbar(this)
    this.bubbles = new Bubbles(app)
    this.animations = new Animations(this)

    this.playing = false
    this.userIsInteracting = false
    this.autoPaused = false
    this.scrollPositionPercent = 0

    gsap.set(this.$bgLayer, { yPercent: 100, opacity: 0 })

    this.$el.addEventListener('touchmove', () => {
      if (this.playing) {
        this.pause()
        this.autoPaused = true
      }
    }, { passive: true })

    Dom.find('[data-start]').addEventListener('click', () => {
      this.toolbar.show()
      this.startParade()
    })

    this.$el.addEventListener('mousedown', e => {
      if (CONFETTI) {
        this.app.confetti.cannonPos = e.offsetX
        this.app.confetti.shoot = true
      }
    })

    this.$el.addEventListener('mouseup', e => {
      if (CONFETTI) {
        this.app.confetti.shoot = false
      }
    })

    this.$btnPlay.addEventListener('click', () => {
      this.app.parade.autoPaused = false
      this.play()
    })

    this.$btnPause.addEventListener('click', () => {
      this.app.parade.autoPaused = false
      this.pause()
    })

    this.initVideo()
  }

  initVideo () {
    this.$video = Dom.find('#video')
    this.$videoTemplate = Dom.find('#VIDEO-SCREEN')

    this.player = new Player(this.$video)
    this.player.on('play', () => { this.toolbar.stopSound() })
    this.player.on('ended', () => { this.toolbar.playSound() })
    this.player.on('pause', () => { this.toolbar.playSound() })
  }

  onResize () {
    this.app.confetti.setCanvasSize()
    this.updateMaxScroll()
    this.updateScrollPositionPercent()
    this.initializeScrollTween()

    this.toolbar.calcIndicatorWidth()
    this.toolbar.moveIndicator()
    this.toolbar.setInitialPlace()

    gsap.delayedCall(0.1, () => this.moveVideo())
  }

  updateScrollPositionPercent () {
    this.scrollPositionPercent = this.$el.scrollLeft / this.maxScroll
  }

  moveVideo () {
    const $videoTemplateRect = this.$videoTemplate.getBoundingClientRect()

    gsap.set(this.$video, {
      duration: 10,
      left: $videoTemplateRect.x + this.$el.scrollLeft,
      top: $videoTemplateRect.y,
      height: $videoTemplateRect.height,
      width: $videoTemplateRect.width,
      autoAlpha: 1
    })
  }

  initializeScrollTween () {
    const $videoTemplateRect = this.$videoTemplate.getBoundingClientRect()
    let videoX

    if (window.innerWidth > $videoTemplateRect.width) {
      videoX = ($videoTemplateRect.x - (window.innerWidth - $videoTemplateRect.width / 2)) + (window.innerWidth / 2)
    } else {
      videoX = $videoTemplateRect.x + (($videoTemplateRect.width - window.innerWidth) / 2)
    }

    videoX += this.$el.scrollLeft

    if (videoX > this.maxScroll) {
      this.videoXProgressTarget = 1
    } else {
      this.videoXProgressTarget = (videoX / this.maxScroll)
    }

    if (this.scrollTween) {
      this.pause()
      this.scrollTween.kill()
      gsap.set(this.$el, { scrollTo: { x: 0, autoKill: false } })
    }

    this.scrollTween = gsap.to(this.$el, {
      scrollTo: { x: this.maxScroll, autoKill: false },
      duration: SCROLL_DURATION, // was 120
      ease: 'none',
      onComplete: () => {
        this.pause()
      }
    })

    this.scrollTween.progress(this.scrollPositionPercent)
    this.scrollTween.timeScale(0)
  }

  startParade () {
    window.addEventListener(Events.APPLICATION_RESIZE, this.onResize.bind(this))
    if (SOUND) {
      this.toolbar.playSound()
    }

    Dom.addClass(this.$el, 'playing')
    this.showBg()

    gsap.to(this.$el, {
      xPercent: '-100',
      ease: 'power2.inOut',
      duration: 1.5,
      onComplete: () => {
        Dom.addClass(Dom.body, 'started')
        this.trapScroll()
        this.bubbles.initialize()
        this.animations.start()
        this.initializeScrollTween()
        if (AUTOPLAY) {
          gsap.delayedCall(0.5, () => this.play())
        }
        if (CONFETTI) {
          gsap.delayedCall(0.5, () => this.app.confetti.start())
        }
        this.moveVideo()
      }
    })
  }

  showBg () {
    gsap.to(this.$bgLayer, { delay: 2.5, yPercent: 0, opacity: 1 })
  }

  trapScroll () {
    this.minScroll = 0
    this.updateMaxScroll()
    this.$el.addEventListener('scroll', rafCallback(this.handleScroll.bind(this)), { passive: true })
  }

  updateMaxScroll () {
    this.maxScroll = this.$el.scrollWidth - this.$el.clientWidth
  }

  handleScroll () {
    this.updateScrollPositionPercent()
    this.toolbar.moveIndicator()
    if (this.app.featureTests.results.ios) {
      gsap.set(this.$bgLayer, { x: (this.$el.scrollLeft / 3) })
    }

    if (this.scrollPositionPercent >= this.videoXProgressTarget) {
      if (this.playing) {
        this.pause()
      }
      this.app.confetti.fireEnd()
    }
  }

  setScrollTweenProgress (posPercent) {
    this.scrollTween.progress(posPercent)
  }

  play () {
    if (this.playing) {
      return
    }
    this.toolbar.indicatorActive = false
    Dom.addClass(this.$playPause, 'playing')
    this.setScrollTweenProgress(this.scrollPositionPercent)
    gsap.to(this.scrollTween, { timeScale: 1, ease: 'sine.in', duration: 1 })
    this.playing = true
  }

  pause () {
    if (!this.playing) {
      return
    }
    Dom.removeClass(this.$playPause, 'playing')
    gsap.to(this.scrollTween, { timeScale: 0, ease: 'sine.in', duration: 0.3 })
    this.playing = false
  }
}
